import { defineStore } from 'pinia';
import { markRaw, ref, shallowRef, type Component } from 'vue';

export type DialogType = 'alert' | 'confirm' | 'prompt' | 'custom';

export interface StandardDialogOptions {
  type: 'alert' | 'confirm' | 'prompt';
  title?: string;
  message: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
}

export interface CustomDialogOptions {
  type: 'custom';
  component: Component;
  props?: Record<string, unknown>;
  closeOnOverlay?: boolean;
}

export type DialogOptions = StandardDialogOptions | CustomDialogOptions;

export type DialogResult = boolean | string | null;

export const useDialogStore = defineStore('dialog', () => {
  const isOpen = ref(false);
  const type = ref<DialogType>('alert');
  const title = ref('提示');
  const message = ref('');
  const defaultValue = ref('');
  const inputValue = ref('');
  const confirmText = ref('确定');
  const cancelText = ref('取消');
  const placeholder = ref('');
  const customComponent = shallowRef<Component | null>(null);
  const customProps = ref<Record<string, unknown>>({});
  const closeOnOverlay = ref(true);

  const resolvePromise = ref<((value: DialogResult) => void) | null>(null);

  const open = (options: DialogOptions): Promise<DialogResult> => {
    type.value = options.type;
    customComponent.value = null;
    customProps.value = {};
    closeOnOverlay.value = true;

    if (options.type === 'custom') {
      title.value = '';
      message.value = '';
      defaultValue.value = '';
      inputValue.value = '';
      confirmText.value = '确定';
      cancelText.value = '取消';
      placeholder.value = '';

      customComponent.value = markRaw(options.component);
      customProps.value = options.props ?? {};
      closeOnOverlay.value = options.closeOnOverlay ?? true;
    } else {
      title.value =
        options.title ?? (options.type === 'confirm' ? '确认' : options.type === 'prompt' ? '输入' : '提示');
      message.value = options.message;
      defaultValue.value = options.defaultValue ?? '';
      inputValue.value = options.defaultValue ?? '';
      confirmText.value = options.confirmText ?? '确定';
      cancelText.value = options.cancelText ?? '取消';
      placeholder.value = options.placeholder ?? '';
    }

    isOpen.value = true;

    return new Promise<DialogResult>(resolve => {
      resolvePromise.value = resolve;
    });
  };

  const openCustom = (
    component: Component,
    options?: { props?: Record<string, unknown>; closeOnOverlay?: boolean },
  ): Promise<DialogResult> => {
    return open({
      type: 'custom',
      component,
      props: options?.props,
      closeOnOverlay: options?.closeOnOverlay,
    });
  };

  const confirm = () => {
    isOpen.value = false;
    customComponent.value = null;
    customProps.value = {};
    if (resolvePromise.value) {
      if (type.value === 'prompt') {
        resolvePromise.value(inputValue.value);
      } else {
        resolvePromise.value(true);
      }
      resolvePromise.value = null;
    }
  };

  const cancel = () => {
    isOpen.value = false;
    customComponent.value = null;
    customProps.value = {};
    if (resolvePromise.value) {
      if (type.value === 'prompt') {
        resolvePromise.value(null);
      } else {
        resolvePromise.value(false);
      }
      resolvePromise.value = null;
    }
  };

  return {
    isOpen,
    type,
    title,
    message,
    inputValue,
    confirmText,
    cancelText,
    placeholder,
    open,
    openCustom,
    customComponent,
    customProps,
    closeOnOverlay,
    confirm,
    cancel,
  };
});
